import { useRef, useState } from 'react';
import Icon from '~/core/ui/Icon';

import { AnimatePresence, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CallOutCard } from '~/components/CallOutCard';
import FancyButton from '~/components/FancyButton';
import configuration from '~/configuration';
import Container from '~/core/ui/Container';
import Heading from '~/core/ui/Heading';
import SubHeading from '~/core/ui/SubHeading';
import { HelpAndSupportCard, cardClassName } from './_site.contact';

export const meta = () => {
  return [
    {
      title: `FAQ - ${configuration.site.siteName}`,
    },
  ];
};

const CATEGORIES = {
  GeneralInformation: 'General Info',
  TechnicalUsage: 'Technical',
  SecurityPrivacy: 'Security and Privacy',
  BillingCredits: 'Billing',
  CustomizationFeatures: 'Features & Customization',
  TheologicalAspects: 'Theology',
  CommunityCollaboration: 'Community',
};

const DATA = [
  {
    question: 'What exactly is FaithGPT?',
    categories: [CATEGORIES.GeneralInformation],
    answer:
      configuration.site.description + ' ' + configuration.site.description2,
  },
  {
    question: 'What is the difference between FaithGPT and ChatGPT?',
    categories: [CATEGORIES.GeneralInformation],
    answer: `<div>FaithGPT ensures your answers don't have an anti-christian bias or a "left" leaning bias. For Christians, you can trust that your answers will be biblically sound. FaithGPT is a faith-focused platform that uses A.I. to enhance Bible study experiences, so you can be confident that the insights and suggestions are in line with scripture.</div>`,
  },
  {
    question: 'Can non-Christians use FaithGPT?',
    categories: [CATEGORIES.GeneralInformation],
    answer: `Yes, FaithGPT is open to everyone. While it's designed to cater to Christian users, anyone can use the platform to explore any topic, biblical stories, theology, and historical context. We also provide a neutral "generalist" assistant that can answer questions about any topic without bias. However, the A.I. assistant's insights and suggestions are based on Christian theology and scripture.`,
  },
  {
    question: 'How does the A.I. chat with biblical figures work?',
    categories: [CATEGORIES.TechnicalUsage, CATEGORIES.TheologicalAspects],
    answer:
      'FaithGPT uses state-of-the-art A.I. algorithms to simulate conversations with biblical figures based on scriptural context, theology, and historical records. This offers users a unique, interactive way to explore biblical stories.',
  },
  {
    question: 'Does FaithGPT offer refunds?',
    categories: [CATEGORIES.BillingCredits],
    answer: `We understand that sometimes things might not work out as expected, and we're here to help. At FaithGPT, we offer a fair refund policy to ensure customer satisfaction:

    <b>Refund Eligibility:</b> If you're not completely satisfied with FaithGPT's services, you can request a refund within 14 days of your purchase.    
    <b>Case-by-Case Evaluation:</b> To ensure fairness, we handle each refund request individually. Our team will review your request to determine eligibility based on your usage and circumstances.    
    <b>Usage-Based Pro-Rating:</b> Please be aware that each interaction on FaithGPT, including chats and conversations, incurs a cost. Therefore, any refund provided will be pro-rated based on your usage of our services. This means the refund amount will reflect the proportion of the service you have used.    
    <b>How to Request a Refund:</b> To initiate a refund, please contact our support team. Provide your account details and the reason for the refund request, and we'll guide you through the process.    
    <b>Further Assistance:</b> If you have any questions or need assistance with your account or our refund policy, please don't hesitate to reach out to our customer support team. We're here to support you and ensure a positive experience with FaithGPT.    
    Our goal is to offer a valuable and satisfying experience to all our users. Your feedback is crucial in helping us improve, so please let us know how we can better serve you.
    `,
    height: '42rem',
  },
  {
    question: 'How secure is our data and personal information?',
    categories: [CATEGORIES.SecurityPrivacy],
    answer:
      'At FaithGPT, user security is paramount. All interactions are encrypted, and we follow industry best practices to ensure your personal data, conversations and discussions remain private and secure. We do not sell or share personal data with third parties, and our commitment to data protection is outlined in our Privacy Policy.',
  },
  {
    question: 'How do the tokens/credits work?',
    categories: [CATEGORIES.BillingCredits, CATEGORIES.TechnicalUsage],
    answer:
      "Each plan comes with a set number of monthly tokens/credits, which are used to access features within the platform. If you run out, you can purchase more. Unused credits renew each month but don't carry over.",
  },
  {
    question: "I'm new to A.I. technology. Is FaithGPT easy to use?",
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      "Absolutely! FaithGPT is designed to be user-friendly, whether you're tech-savvy or new to A.I. It's as simple as sending a text message. Our intuitive user interfaces and clear instructions, anyone can start their enriched Bible study journey with ease.",
  },
  {
    question: 'Does FaithGPT replace attending church or group studies?',
    categories: [
      CATEGORIES.TheologicalAspects,
      CATEGORIES.CommunityCollaboration,
    ],
    answer:
      "No, FaithGPT is a supplementary tool. It's designed to enhance personal and group study experiences, not replace communal worship or the fellowship of church gatherings.",
  },
  {
    question:
      'Is FaithGPT associated with any particular Christian denomination?',
    categories: [CATEGORIES.GeneralInformation, CATEGORIES.TheologicalAspects],
    answer:
      "No, FaithGPT is designed to be a tool for all Christians, irrespective of denomination. It provides information based on the Bible and doesn't favor any particular denomination's doctrines.",
  },
  {
    question: 'Can I create my own custom A.I. powered chatbot?',
    categories: [CATEGORIES.CustomizationFeatures],
    answer:
      'Yes, FaithGPT offers a chatbot builder that allows you to create your own custom chatbots. You can use them for personal study or share them with others. More instructions are available within our platform.',
  },
  {
    question: 'Can I share my custom templates with others?',
    categories: [
      CATEGORIES.CustomizationFeatures,
      CATEGORIES.CommunityCollaboration,
    ],
    answer:
      'Yes, FaithGPT offers collaborative features that allow you to share your templates with others. All you need to do is invite them to your team, and they can access your templates.',
  },
  {
    question: 'How does the platform ensure accurate theological insights?',
    categories: [CATEGORIES.TechnicalUsage, CATEGORIES.TheologicalAspects],
    answer:
      'FaithGPT combines advanced A.I. algorithms with vetted theological databases and resources. While it offers robust insights, users are always encouraged to cross-reference with trusted theological sources or advisors.',
  },
  {
    question: 'What if I have suggestions or feedback for the platform?',
    categories: [CATEGORIES.GeneralInformation],
    answer:
      "We value user feedback! There's a dedicated section within the platform for users to share suggestions, report issues, or provide feedback. We continuously work to enhance FaithGPT based on our user's needs.",
  },
  {
    question: 'Can FaithGPT be used by Bible study groups or just individuals?',
    categories: [CATEGORIES.TechnicalUsage, CATEGORIES.CommunityCollaboration],
    answer:
      'FaithGPT is versatile and can be used both by individuals for personal reflection and by Bible study groups for collaborative exploration and discussion.',
  },
  {
    question: 'How often is the A.I. updated or improved?',
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      'We are committed to providing the best experience for our users. Our A.I. models and algorithms are updated regularly to ensure accuracy and improved user interaction.',
  },
  {
    question: 'Is there a mobile version or app of FaithGPT?',
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      "Currently, FaithGPT is web-based, but it's optimized for all devices, including mobiles and tablets. We're always exploring ways to enhance accessibility, including potential mobile apps in the future.",
  },
  {
    question: 'How do I purchase additional tokens/credits if I run out?',
    categories: [CATEGORIES.BillingCredits],
    answer:
      'Within the platform, there\'s an option to buy more tokens whenever you need them. Just navigate to the "Settings" section, and follow the purchase instructions.',
  },
  {
    question:
      'Will FaithGPT influence my beliefs or interpret the Bible for me?',
    categories: [CATEGORIES.TheologicalAspects],
    answer:
      "FaithGPT provides tools and insights to help you explore the Bible in depth. It doesn't seek to influence beliefs but rather offers perspectives based on scripture. Ultimately, personal beliefs and interpretations are up to individual users and the work of the Holy Spirit.",
  },
  {
    question:
      'Is there customer support if I face any issues or have questions?',
    categories: [CATEGORIES.GeneralInformation],
    answer:
      'Yes, we offer dedicated customer support to assist with any concerns, issues, or questions you might have. Our team is available through chat, email (hello@faithgpt.io), and other channels provided on the platform.',
  },
  {
    question: 'Does FaithGPT support multiple languages?',
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      'Yes, right now, we support English, French, Spanish, and Italian. We hope to support many more languages in the future. Feel free to request your language on our feature requests page and upvote.',
  },
  {
    question:
      'I lead a large congregation. Are there special plans or pricing for bulk users?',
    categories: [CATEGORIES.BillingCredits, CATEGORIES.CommunityCollaboration],
    answer:
      'Yes, we offer the "Ministy" plan tailored for large groups and congregations. If you have specific needs or require a custom solution, please contact us for more details. We\'ll do our best to accommodate.',
  },
  {
    question:
      'How does FaithGPT handle controversial or disputed theological topics?',
    categories: [CATEGORIES.TheologicalAspects],
    answer:
      'FaithGPT provides insights and perspectives based on a wide range of theological sources. For topics that have varied interpretations, the platform aims to present a balanced view, highlighting different perspectives where possible.',
  },
  {
    question:
      'Is my study progress saved, and can I revisit previous sessions?',
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      'Yes, your study progress and interactions are saved. You can revisit previous sessions, search through your conversations and chats anytime for insights, ensuring continuity in your exploration.',
  },
  {
    question: 'Why mix faith with A.I.?',
    categories: [CATEGORIES.GeneralInformation, CATEGORIES.TheologicalAspects],
    answer:
      "Integrating A.I. with faith-focused study offers a modern approach to exploring scriptures. It allows for personalized experiences, in-depth contextual insights, and interactive engagements, making scripture study more accessible and enriching for today's digital age.",
  },
  {
    question: 'Can the results and suggestions be trusted?',
    categories: [CATEGORIES.TheologicalAspects],
    answer:
      'FaithGPT provides insights and suggestions based on a vast database of theological sources and scriptures. While it offers a robust platform for exploration, users are always encouraged to cross-reference with trusted theological sources or advisors.',
  },
  {
    question: 'Can I request the deletion of my data from FaithGPT?',
    categories: [CATEGORIES.SecurityPrivacy],
    answer:
      'Absolutely. We respect your right to privacy and data ownership. Users can request the deletion of their personal data at any time. Upon such a request, we will promptly remove your information from our servers in compliance with data protection laws. To initiate a data deletion request, please contact our support team, and we will guide you through the process, ensuring a smooth and secure removal of your data.',
  },

  {
    question: 'How does the A.I. assistant work?',
    categories: [CATEGORIES.TechnicalUsage],
    answer:
      'The A.I. assistant uses state-of-the-art algorithms to understand user queries and provide contextually relevant responses. It analyzes scriptures, theological databases, and historical records to offer insightful feedback.',
  },
  {
    question: 'Is the A.I. assistant able to provide personalized prayers?',
    categories: [
      CATEGORIES.CustomizationFeatures,
      CATEGORIES.TheologicalAspects,
    ],
    answer:
      "Yes, the A.I. assistant can craft personalized prayers based on user inputs and preferences. However, it's always important to remember that the essence of prayer is personal connection and intention, so while the A.I. can assist, heartfelt personal prayers are irreplaceable.",
  },
  {
    question:
      'How does FaithGPT handle sensitive information shared during conversations?',
    categories: [CATEGORIES.SecurityPrivacy],
    answer:
      'We understand the importance of confidentiality, especially when sensitive information is shared. FaithGPT treats all user conversations with the utmost privacy. Our platform is designed to encrypt conversation data, ensuring that personal details or sensitive information cannot be traced back to any individual. Additionally, we have strict policies in place to prevent the misuse of any information shared during conversations.',
  },
  {
    question:
      "Can users customize the A.I. assistant's responses to be in line with their specific beliefs?",
    categories: [
      CATEGORIES.CustomizationFeatures,
      CATEGORIES.TheologicalAspects,
    ],
    answer:
      "Absolutely! FaithGPT offers customization options allowing users to tailor the A.I. assistant's responses to align more closely with their personal beliefs and denominational teachings.",
  },
  {
    question: 'Can I suggest a feature?',
    categories: [
      CATEGORIES.GeneralInformation,
      CATEGORIES.CommunityCollaboration,
    ],
    answer:
      "Definitely! We value user feedback and are always looking to enhance FaithGPT. There's a dedicated section within the platform for users to suggest features, report issues, or provide general feedback. You can also upvote requested features to help us prioritize.",
  },
  {
    question: 'Are there plans to expand to other religious texts?',
    categories: [CATEGORIES.GeneralInformation],
    answer:
      "FaithGPT focuses on Christian scriptures. However, we're always exploring ways to broaden and enrich the platform. Stay tuned for updates and announcements.",
  },
  {
    question: 'How often is new content or updates added to the platform?',
    categories: [CATEGORIES.GeneralInformation],
    answer:
      'We aim to keep FaithGPT at the forefront of technological and theological developments. Updates, new features, and content are rolled out regularly based on user feedback and ongoing research. For deeper dives on various issues, check out our blog.',
  },
  {
    question:
      'Is FaithGPT suitable for academic research or theological studies?',
    categories: [CATEGORIES.GeneralInformation, CATEGORIES.TheologicalAspects],
    answer:
      'Absolutely! While FaithGPT is designed to be accessible for all users, it also offers in-depth insights, contextual annotations, and collaborative tools, making it a valuable resource for academic and theological explorations.',
  },
];

const Faq = ({ homepage = false, pricingPage = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const showMinimalFAQ = homepage || pricingPage;
  const itemsPerPage = showMinimalFAQ ? 7 : 10;
  const { ref, inView } = useInView({ threshold: 0.4, triggerOnce: true });

  const filteredData = DATA.filter(
    (item) =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.categories.some(
        (category) => category.toLowerCase() === searchTerm.toLowerCase(),
      ),
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: DATA.map((item) => {
      return {
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      };
    }),
  };

  return (
    <div
      className={
        !showMinimalFAQ || pricingPage
          ? 'mx-auto max-w-7xl'
          : 'opacity-0 mb-48' + (inView ? ' animate-fade-in' : '')
      }
      ref={ref}
    >
      <script
        key={'ld:json'}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Container
        className={
          'flex flex-col space-y-8 my-8 ' + (showMinimalFAQ ? '' : 'px-5')
        }
        forceAnimate={!showMinimalFAQ}
      >
        <div
          className={`flex flex-col items-center space-y-4 ${showMinimalFAQ ? 'pb-7' : 'pb-12 border-b border-gray-300 dark:border-dark-600'}`}
        >
          <Heading>Frequently Asked Questions</Heading>
          <SubHeading>
            Get answers to common questions about FaithGPT
          </SubHeading>
          {!showMinimalFAQ && (
            <input
              type="text"
              placeholder="Search through our frequently asked questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-4 !mt-12 !mb-4 h-[3.2rem] border-[3px] border-[#212a37] dark:border-white rounded-2xl w-full max-w-3xl focus:outline-none focus:ring-2 focus:ring-secondary-500 bg-transparent faq-drop-shadow"
            />
          )}
        </div>

        <div className="flex gap-16 justify-center">
          {!showMinimalFAQ && (
            <div className="hidden md:block w-[20rem]">
              <div className="sticky top-24">
                <div className="flex flex-col space-y-4">
                  <h2 className="font-bold text-lg mt-8">Categories</h2>
                  <p className="flex flex-col flex-wrap gap-2">
                    {Object.values(CATEGORIES).map((category, index) => {
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            if (searchTerm === category) {
                              setSearchTerm('');
                              setCurrentPage(1);
                              return;
                            }
                            setSearchTerm(category);
                            setCurrentPage(1);
                          }}
                          className="hover:underline flex items-center text-left"
                          style={{
                            color:
                              searchTerm === category ? '#f59e0b' : 'inherit',
                          }}
                        >
                          {category}
                          {searchTerm === category && (
                            <span className="ml-2 mt-[1px] text-xs hover:text-secondary-700 cursor-pointer">
                              <Icon
                                icon="line-md:arrow-small-right"
                                width="1rem"
                                className="flex items-center"
                              />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </p>
                </div>
                <div className="mt-16">
                  <HelpAndSupportCard />
                </div>
              </div>
            </div>
          )}
          <div className="w-full max-w-5xl mx-auto">
            <div
              className={`m-auto md:!m-0 flex w-full ${showMinimalFAQ ? 'max-w-5xl' : 'max-w-4xl'} md:min-w-[36rem] items-center justify-center`}
            >
              <div className="flex w-full flex-col">
                {filteredData.length > 0 ? (
                  filteredData
                    .slice(
                      (currentPage - 1) * itemsPerPage,
                      currentPage * itemsPerPage,
                    )
                    .map((item, index) => {
                      return (
                        <FaqItem
                          key={index}
                          item={item}
                          homepage={showMinimalFAQ}
                        />
                      );
                    })
                ) : (
                  <p className="text-center mt-8 bg-black/10 dark:bg-white/10 p-4 my-12 m-auto">
                    No results found :(
                  </p>
                )}
              </div>
            </div>
            {showMinimalFAQ ? (
              <FancyButton
                variant="outline"
                href="/faq"
                containerClassName="!mx-auto !flex !justify-center !w-fit"
                className="!mt-12 !block text-center font-medium justify-center items-center gap-2 max-w-full w-auto m-[5vw] my-0 !px-8 rounded-[4rem] border-2 border-solid border-[#f59e0b] hover:!text-white !py-3"
              >
                See more{' '}
                <span className="hidden md:inline !m-0">
                  Frequently Asked Questions
                </span>{' '}
                <span className="inline md:hidden !m-0">FAQs</span>
                {/* <Icon
                  icon="mdi:external-link"
                  width="1.25rem"
                  height="1.25rem"
                /> */}
              </FancyButton>
            ) : (
              <>
                {totalPages > 1 && (
                  <div className="flex justify-start space-x-1 !mt-16 !mb-[4.5rem]">
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`${
                          currentPage === index + 1
                            ? 'bg-secondary-500 text-black font-bold'
                            : 'font-bold bg-secondary-500/0 hover:bg-secondary-500 hover:text-black'
                        } p-1 h-10 w-12 rounded transition-colors duration-300`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Container>

      {!showMinimalFAQ && (
        <Container animate forceAnimate>
          <CallOutCard forceButtonHover />
        </Container>
      )}
    </div>
  );
};

export default Faq;

function FaqItem({
  item,
  homepage,
}: React.PropsWithChildren<{
  item: {
    question: string;
    answer: string;
    categories: string[];
    height?: string;
  };
  homepage?: boolean;
}>) {
  const [open, setOpen] = useState(false);
  const titleRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen(!open);
  };

  return (
    <motion.details
      className={
        'group border-b border-gray-100 dark:border-dark-700 overflow-hidden'
      }
      initial={{ height: '5.5rem' }}
      animate={{
        height: open
          ? `${(contentRef.current?.scrollHeight || 0) + (titleRef.current?.scrollHeight || 0) + 40}px`
          : '5.5rem',
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <summary
        className={
          'flex items-center justify-between hover:cursor-pointer h-[88px]'
        }
        onClick={handleToggle}
        ref={titleRef}
      >
        <h2
          className={
            'flex justify-between items-center w-[94%] font-sans text-lg font-medium text-gray-700' +
            ' hover:underline-none group-open:text-secondary relative' +
            ' cursor-pointer dark:text-gray-300 dark:hover:text-white'
          }
          style={{
            flexWrap: !open ? 'nowrap' : 'wrap',
            gap: !open ? '0' : '1rem',
          }}
        >
          <div
            className="flex-grow overflow-hidden overflow-ellipsis line-clamp-2 mr-3"
            style={{
              lineClamp: !open ? 2 : 3,
              width: !open ? '100%' : 'auto',
            }}
          >
            {item.question}
          </div>
          <AnimatePresence>
            {!open && (
              <motion.div
                className="hidden md:inline-block flex-grow overflow-hidden text-ellipsis w-auto"
                initial={{
                  opacity: 0,
                  translateX: '-30%',
                  translateY: homepage ? 70 : 40,
                  position: 'absolute',
                  right: 0,
                  scale: 0.8,
                }}
                animate={{
                  opacity: 1,
                  translateX: 0,
                  translateY: 0,
                  position: 'relative',
                  scale: 1,
                  transition: { duration: 0.3, ease: 'easeInOut' },
                }}
                exit={{
                  opacity: 0,
                  translateX: '-30%',
                  translateY: homepage ? 70 : 40,
                  position: 'absolute',
                  right: 0,
                  scale: 0.8,
                }}
              >
                <div
                  className="overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-500 dark:text-gray-400 opacity-50 transition-opacity duration-300"
                  style={{
                    maskImage:
                      'linear-gradient(to right, black 50%, transparent 100%)',
                  }}
                >
                  {item.answer
                    .replace(/(<([^>]+)>)/gi, '')
                    .substring(0, homepage ? 120 : 100 - item.question.length)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <span className="hidden md:inline-block mt-[1px] text-xs hover:text-secondary-700 cursor-pointer">
            <span
              className={
                cardClassName + '!p-2 opacity-70 mr-1 whitespace-nowrap'
              }
            >
              {item.categories[0]}
            </span>
            {!open && item.categories.length > 1 && (
              <span
                className={
                  cardClassName + '!p-2 opacity-70 mr-1 whitespace-nowrap'
                }
              >
                +{item.categories.length - 1}
              </span>
            )}
            {open &&
              item.categories.slice(1).map((category, index) => (
                <span
                  key={index}
                  className={
                    cardClassName + '!p-2 opacity-70 mr-1 whitespace-nowrap'
                  }
                >
                  {category}
                </span>
              ))}
          </span>
        </h2>

        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="1.5rem"
            height="1.5rem"
            className="transition duration-500 scale-125"
          >
            <path
              className={`origin-center transition duration-500 ${
                open ? 'scale-0 rotate-[360deg]' : 'scale-100'
              }`}
              d="M12 5.25a.75.75 0 01.75.75v5.25H18a.75.75 0 010 1.5h-5.25V18a.75.75 0 01-1.5 0v-5.25H6a.75.75 0 010-1.5h5.25V6a.75.75 0 01.75-.75z"
            />
            <path
              className={`origin-center transition duration-500 ${
                open ? 'scale-100' : 'scale-0'
              }`}
              d="M6 12a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H6.75A.75.75 0 016 12z"
            />
          </svg>
        </div>
      </summary>

      <motion.div
        className={
          'flex flex-col space-y-2 mb-5 pb-1 text-gray-500 dark:text-gray-400 leading-7'
        }
        dangerouslySetInnerHTML={{
          __html: item.answer.replace(/\n/g, '<br />'),
        }}
        ref={contentRef}
        style={{
          height: open ? contentRef.current?.scrollHeight : 0,
          transition: 'height 0.3s ease',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />
    </motion.details>
  );
}
